import React, { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { hasConsent, onConsentChange, flushQueuedEvents } from '../utils/analytics';

const AnalyticsIntegration = ({ 
  googleAnalyticsId,
  googleTagManagerId,
  hotjarId,
  facebookPixelId,
  tiktokPixelId,
  enableCookieConsent = true
}) => {
  const [allowed, setAllowed] = useState(() => !enableCookieConsent || hasConsent());

  useEffect(() => onConsentChange(() => {
    setAllowed(!enableCookieConsent || hasConsent());
  }), [enableCookieConsent]);

  // gtag arrives asynchronously, so drain anything recorded before it existed.
  useEffect(() => {
    if (!allowed) return undefined;
    const timer = setInterval(flushQueuedEvents, 500);
    return () => clearInterval(timer);
  }, [allowed]);

  // Injected directly rather than through Helmet. Helmet manages head metadata
  // and its timing depends on hydration; a tracker should not.
  useEffect(() => {
    if (!allowed || !googleAnalyticsId) return;
    if (document.getElementById('ga4-src')) return;

    const loader = document.createElement('script');
    loader.id = 'ga4-src';
    loader.async = true;
    loader.src = `https://www.googletagmanager.com/gtag/js?id=${googleAnalyticsId}`;
    document.head.appendChild(loader);

    window.dataLayer = window.dataLayer || [];
    window.gtag = function gtag() { window.dataLayer.push(arguments); };
    window.gtag('js', new Date());
    window.gtag('config', googleAnalyticsId, {
      anonymize_ip: true,
      cookie_flags: 'secure;samesite=strict',
    });
    flushQueuedEvents();
  }, [allowed, googleAnalyticsId]);

  if (!allowed) return null;

  return (
    <Helmet>
      {/* Google Analytics 4 is injected in the effect above, not here. */}

      {/* Google Tag Manager */}
      {googleTagManagerId && (
        <script>
          {`
              (function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start':
              new Date().getTime(),event:'gtm.js'});var f=d.getElementsByTagName(s)[0],
              j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';j.async=true;j.src=
              'https://www.googletagmanager.com/gtm.js?id='+i+dl;f.parentNode.insertBefore(j,f);
              })(window,document,'script','dataLayer','${googleTagManagerId}');
            `}
        </script>
      )}

      {/* Hotjar */}
      {hotjarId && (
        <script>
          {`
            (function(h,o,t,j,a,r){
                h.hj=h.hj||function(){(h.hj.q=h.hj.q||[]).push(arguments)};
                h._hjSettings={hjid:${hotjarId},hjsv:6};
                a=o.getElementsByTagName('head')[0];
                r=o.createElement('script');r.async=1;
                r.src=t+h._hjSettings.hjid+j+h._hjSettings.hjsv;
                a.appendChild(r);
            })(window,document,'https://static.hotjar.com/c/hotjar-','.js?sv=');
          `}
        </script>
      )}

      {/* Facebook Pixel */}
      {facebookPixelId && (
        <script>
          {`
            !function(f,b,e,v,n,t,s)
            {if(f.fbq)return;n=f.fbq=function(){n.callMethod?
            n.callMethod.apply(n,arguments):n.queue.push(arguments)};
            if(!f._fbq)f._fbq=n;n.push=n;n.loaded=!0;n.version='2.0';
            n.queue=[];t=b.createElement(e);t.async=!0;
            t.src=v;s=b.getElementsByTagName(e)[0];
            s.parentNode.insertBefore(t,s)}(window, document,'script',
            'https://connect.facebook.net/en_US/fbevents.js');
            fbq('init', '${facebookPixelId}');
            fbq('track', 'PageView');
          `}
        </script>
      )}

      {/* TikTok Pixel */}
      {tiktokPixelId && (
        <script>
          {`
            !function (w, d, t) {
              w.TiktokAnalyticsObject=t;var ttq=w[t]=w[t]||[];ttq.methods=["page","track","identify","instances","debug","on","off","once","ready","alias","group","enableCookie","disableCookie"],ttq.setAndDefer=function(t,e){t[e]=function(){t.push([e].concat(Array.prototype.slice.call(arguments,0)))}};for(var i=0;i<ttq.methods.length;i++)ttq.setAndDefer(ttq,ttq.methods[i]);ttq.instance=function(t){for(var e=ttq._i[t]||[],n=0;n<ttq.methods.length;n++)ttq.setAndDefer(e,ttq.methods[n]);return e},ttq.load=function(e,n){var i="https://analytics.tiktok.com/i18n/pixel/events.js";ttq._i=ttq._i||{},ttq._i[e]=[],ttq._i[e]._u=i,ttq._t=ttq._t||{},ttq._t[e]=+new Date,ttq._o=ttq._o||{},ttq._o[e]=n||{};var o=document.createElement("script");o.type="text/javascript",o.async=!0,o.src=i+"?sdkid="+e+"&lib="+t;var a=document.getElementsByTagName("script")[0];a.parentNode.insertBefore(o,a)};
              ttq.load('${tiktokPixelId}');
              ttq.page();
            }(window, document, 'ttq');
          `}
        </script>
      )}

      {/* Performance monitoring is handled by WebVitalsTracker component */}

      {/* Consent is owned by the CookieConsent component. A second banner used to
          be injected from here on window load - a duplicate, on a different
          storage key, that never ran because load had already fired. */}
    </Helmet>
  );
};

export default AnalyticsIntegration;